import { IconProps } from "@chakra-ui/icons";
import { User } from "@stytch/vanilla-js";
import i18n from "i18n/config";
import { MessageDotsSquareIcon } from "shared/icons/MessageDotsSquareIcon";
import { QRCodeIcon } from "shared/icons/QrCodeIcon";
import { USBFlashDriveIcon } from "shared/icons/USBFlashDriveIcon";
import { WarningIcon } from "shared/icons/WarningIcon";

export type AuthFactorType = "totp" | "webauthn" | "otp" | "recovery";

export type AuthFactorInstance =
  | TotpAuthFactorInstance
  | OtpAuthFactorInstance
  | WebauthnAuthFactorInstance
  | RecoveryAuthFactorInstance;

export interface AuthFactorInfo {
  type: AuthFactorType;
  name: string;
  icon: React.ComponentType<IconProps>;
}

export type AuthFactorInstanceData =
  | OtpAuthFactorInstanceData
  | TotpAuthFactorInstanceData
  | WebauthnAuthFactorInstanceData
  | RecoveryAuthFactorInstanceData;

export const TotpAuthFactorInfo: AuthFactorInfo = {
  type: "totp",
  name: i18n.t("auth:authenticatorApp"),
  icon: QRCodeIcon,
};

export const OtpAuthFactorInfo: AuthFactorInfo = {
  type: "otp",
  name: i18n.t("auth:textMessage"),
  icon: MessageDotsSquareIcon,
};

export const WebauthnAuthFactorInfo: AuthFactorInfo = {
  type: "webauthn",
  name: i18n.t("auth:securityKey"),
  icon: USBFlashDriveIcon,
};

export const RecoveryAuthFactorInfo: AuthFactorInfo = {
  type: "recovery",
  name: i18n.t("auth:recover"),
  icon: WarningIcon,
};

export const AuthFactorInfos = {
  totp: TotpAuthFactorInfo,
  otp: OtpAuthFactorInfo,
  webauthn: WebauthnAuthFactorInfo,
  recovery: RecoveryAuthFactorInfo,
};

export interface AuthFactorSetupProps {
  onCancel: () => void;
  onSuccess: () => void;
  rememberDevice?: boolean;
}

export interface AuthFactorInstanceProps<TInstance extends AuthFactorInstance>
  extends AuthFactorSetupProps {
  instance: TInstance;
  onSelectAuthFactor: (instance: AuthFactorInstance) => void;
}

interface BaseAuthFactorInstance<
  TType extends AuthFactorType,
  TData extends AuthFactorInstanceData,
> {
  id: string;
  type: TType;
  data: TData;
  name?: string;
}

export type TotpAuthFactorInstance = BaseAuthFactorInstance<
  "totp",
  TotpAuthFactorInstanceData
>;
export type OtpAuthFactorInstance = BaseAuthFactorInstance<
  "otp",
  OtpAuthFactorInstanceData
>;
export type WebauthnAuthFactorInstance = BaseAuthFactorInstance<
  "webauthn",
  WebauthnAuthFactorInstanceData
>;
export type RecoveryAuthFactorInstance = BaseAuthFactorInstance<
  "recovery",
  RecoveryAuthFactorInstanceData
>;

type OmitVerified<T> = Omit<T, "verified">;

export type OtpAuthFactorInstanceData = OmitVerified<User["phone_numbers"][0]>;
export type TotpAuthFactorInstanceData = OmitVerified<User["totps"][0]>;
export type WebauthnAuthFactorInstanceData = OmitVerified<
  User["webauthn_registrations"][0]
>;
export type RecoveryAuthFactorInstanceData = Record<string, string>;
